import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ClientLanguage, ClientLanguageService } from 'src/shared-utilities/services/client-language.service';

import { OptionGroupLoadTime, OptionsGroup, ResourceTypeLoadTime } from './options.models';
import { APP_CONFIG, AppConfig } from 'src/environments/app-environment.models';

@Injectable({
  providedIn: 'root',
})
export class OptionsCache {
  private readonly appConfig = inject<AppConfig>(APP_CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly clientLanguageService = inject(ClientLanguageService);

  constructor() {}

  // cached options groups
  private optionsGroups: Array<OptionsGroup> = [];

  private optionGroupLoadTimes: Array<OptionGroupLoadTime> = [];
  private resourceTypeLoadTimes: Array<ResourceTypeLoadTime> = [];

  // Options Groups are loaded into cache when the loadResourceOptions or
  // loadOptionGroup methods are called. The reloadCacheSeconds value is
  // used to determine when a reload is valid or should be skipped.
  private readonly reloadCacheMilliseconds: number = 30 * 60 * 1000; // 30 minutes

  // Adds resources to the cache
  async loadResourceOptions(resourceType: string, force = false): Promise<void> {
    // Try-get the loadTime object for the given reousrce type
    const normalizedResourceType = resourceType.toLowerCase();
    const loadTime = this.resourceTypeLoadTimes.find(t => t.resourceType.toLocaleLowerCase() == normalizedResourceType);

    // If the load is not being forced, check if enough
    // time has passed since the last load, and only
    // proceed if necessary. If it hasn't, bail.
    if (loadTime && !force) {
      const now = new Date();
      const millisecondsSinceLastLoad = now.getTime() - loadTime.time.getTime();
      if (millisecondsSinceLastLoad < this.reloadCacheMilliseconds) return;
    }

    const url = `${this.appConfig.publicApiBaseUrl}/v2/options/resource/${resourceType}/${
      this.clientLanguageService.clientLanguage ?? ClientLanguage.en
    }`;
    const optionsGroups = await lastValueFrom(this.httpClient.get<Array<OptionsGroup>>(url));

    optionsGroups.forEach(g => this.addGroupToCache(g));

    const loadTimeIndex = this.resourceTypeLoadTimes.findIndex(
      t => t.resourceType.toLocaleLowerCase() == normalizedResourceType,
    );
    if (loadTimeIndex != -1) {
      this.resourceTypeLoadTimes.splice(loadTimeIndex, 1);
    }
    this.resourceTypeLoadTimes.push({
      resourceType: resourceType,
      time: new Date(),
    });
  }

  // TODO: loadOptionGroup (get the option group from the DB) and add it to the cache

  private addGroupToCache(optionsGroup: OptionsGroup) {
    // Attempt to remove any group that might
    // overlap in name before adding the new group
    this.removeOptionsGroup(optionsGroup);
    this.optionsGroups.push(optionsGroup);

    const normalizedGroupName = optionsGroup.groupName.toLowerCase();
    const loadTimeIndex = this.optionGroupLoadTimes.findIndex(
      t => t.groupName.toLocaleLowerCase() == normalizedGroupName,
    );
    if (loadTimeIndex != -1) {
      this.optionGroupLoadTimes.splice(loadTimeIndex, 1);
    }
    this.optionGroupLoadTimes.push({
      groupName: optionsGroup.groupName,
      time: new Date(),
    });
  }

  removeOptionsGroup(group: string | OptionsGroup) {
    let groupName: string;
    if (typeof group == 'string') {
      groupName = group;
    } else {
      groupName = group.groupName;
    }

    const normalizedGroupName = groupName.toLowerCase();
    const groupIndex = this.optionsGroups.findIndex(g => g.groupName.toLowerCase() == normalizedGroupName);
    if (groupIndex != -1) {
      this.optionsGroups.splice(groupIndex, 1);
    }

    const loadTimeIndex = this.optionGroupLoadTimes.findIndex(t => t.groupName.toLowerCase() == normalizedGroupName);
    if (loadTimeIndex != -1) {
      this.optionGroupLoadTimes.splice(loadTimeIndex, 1);
    }
  }

  getOptionsGroup(groupName: string): OptionsGroup {
    const normalizedGroupName = groupName.toLowerCase();
    const group = this.optionsGroups.find(g => g.groupName.toLowerCase() == normalizedGroupName);

    // TODO: loadOptionGroup (get the option group from the DB) if it wasn't found

    return group;
  }
}
